import { Fragment } from "react";
import { designTokens } from "utils/designSystem";

const PALETTE = [
  "#3b82f6",
  "#f59e0b",
  "#10b981",
  "#ef4444",
  "#8b5cf6",
  "#ec4899",
  "#06b6d4",
  "#84cc16",
  "#f97316",
  "#6366f1",
  "#14b8a6",
  "#a855f7",
];

const SIZE = 200;
const STROKE = 34;
const RADIUS = (SIZE - STROKE) / 2;
const CENTER = SIZE / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

const styles = {
  wrapper: {
    display: "flex",
    alignItems: "center",
    gap: "32px",
    flexWrap: "wrap",
  },
  chartBox: {
    position: "relative",
    width: `${SIZE}px`,
    height: `${SIZE}px`,
    flexShrink: 0,
  },
  centerText: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
  },
  centerValue: {
    fontSize: "28px",
    fontWeight: "700",
    color: designTokens.colors.text.primary,
    lineHeight: 1.1,
  },
  centerLabel: {
    fontSize: "13px",
    color: designTokens.colors.text.tertiary,
    marginTop: "2px",
  },
  centerValueLong: {
    fontSize: "20px",
  },
  legend: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    minWidth: "220px",
    flex: 1,
  },
  legendRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontSize: "14px",
    padding: "6px 8px",
    borderRadius: "6px",
    transition: "background-color 0.15s ease",
  },
  legendRowClickable: {
    cursor: "pointer",
  },
  swatch: {
    width: "12px",
    height: "12px",
    borderRadius: "3px",
    flexShrink: 0,
  },
  legendLabel: {
    color: designTokens.colors.text.secondary,
    flex: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  valueBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  },
  legendValue: {
    color: designTokens.colors.text.primary,
    fontWeight: "600",
    whiteSpace: "nowrap",
  },
  legendSub: {
    fontSize: "11px",
    color: designTokens.colors.text.muted,
    whiteSpace: "nowrap",
  },
  divider: {
    height: "1px",
    backgroundColor: designTokens.colors.borderLight,
    margin: "4px 8px",
  },
  legendPercent: {
    color: designTokens.colors.text.tertiary,
    fontWeight: "500",
    minWidth: "52px",
    textAlign: "right",
  },
  empty: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: `${SIZE}px`,
    color: designTokens.colors.text.muted,
    fontSize: "14px",
  },
};

const DonutChart = ({ data = [], onSelect, unit = "명", percentBase, centerValue, centerLabel }) => {
  const clickable = typeof onSelect === "function";
  const items = data.filter((d) => d && d.count > 0);
  const total = items.reduce((sum, d) => sum + d.count, 0);
  const base = percentBase || total;

  if (total === 0) {
    return <div style={styles.empty}>데이터가 없습니다.</div>;
  }

  let offsetAcc = 0;
  const segments = items.map((item, idx) => {
    const color = item.color || PALETTE[idx % PALETTE.length];
    const fraction = item.count / total;
    const length = fraction * CIRCUMFERENCE;
    const segment = {
      ...item,
      color,
      percent: (item.count / base) * 100,
      dashArray: `${length} ${CIRCUMFERENCE - length}`,
      dashOffset: CIRCUMFERENCE - offsetAcc,
    };
    offsetAcc += length;
    return segment;
  });

  const shownCenter = centerValue ?? total.toLocaleString();

  return (
    <div style={styles.wrapper}>
      <div style={styles.chartBox}>
        <svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
          <g transform={`rotate(-90 ${CENTER} ${CENTER})`}>
            <circle
              cx={CENTER}
              cy={CENTER}
              r={RADIUS}
              fill="none"
              stroke={designTokens.colors.borderLight}
              strokeWidth={STROKE}
            />
            {segments.map((seg) => (
              <circle
                key={seg.label}
                cx={CENTER}
                cy={CENTER}
                r={RADIUS}
                fill="none"
                stroke={seg.color}
                strokeWidth={STROKE}
                strokeDasharray={seg.dashArray}
                strokeDashoffset={seg.dashOffset}
                onClick={clickable ? () => onSelect(seg) : undefined}
                style={{ cursor: clickable ? "pointer" : "default" }}
              />
            ))}
          </g>
        </svg>
        <div style={styles.centerText}>
          <span
            style={{
              ...styles.centerValue,
              ...(String(shownCenter).length > 6 ? styles.centerValueLong : {}),
            }}
          >
            {shownCenter}
          </span>
          <span style={styles.centerLabel}>
            {centerLabel ?? `총 ${total.toLocaleString()}${unit}`}
          </span>
        </div>
      </div>

      <div style={styles.legend}>
        {segments.map((seg) => (
          <Fragment key={seg.label}>
            {seg.separated && <div data-testid="donut-divider" style={styles.divider} />}
            <div
              className={clickable ? "donut-legend-row" : undefined}
              style={{
                ...styles.legendRow,
                ...(clickable ? styles.legendRowClickable : {}),
              }}
              onClick={clickable ? () => onSelect(seg) : undefined}
            >
              <span style={{ ...styles.swatch, backgroundColor: seg.color }} />
              <span style={styles.legendLabel} title={seg.label}>
                {seg.label}
              </span>
              <span style={styles.valueBox}>
                <span style={styles.legendValue}>
                  {seg.count.toLocaleString()}{unit}
                </span>
                {seg.sub && <span style={styles.legendSub}>{seg.sub}</span>}
              </span>
              <span style={styles.legendPercent}>{seg.percent.toFixed(1)}%</span>
            </div>
          </Fragment>
        ))}
      </div>
      {clickable && (
        <style>{`.donut-legend-row:hover{background-color:#f1f5f9;}`}</style>
      )}
    </div>
  );
};

export default DonutChart;
